import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pookieApi";
import { Gavel, Scale, Quote, Lock, Sparkles } from "lucide-react";

const StatBlock = ({ label, value, bg, testid }) => (
  <div
    data-testid={testid}
    className={`pl-card-flat ${bg} p-5 md:p-6 flex flex-col gap-1`}
  >
    <div className="font-display text-4xl md:text-5xl leading-none">{value}</div>
    <div className="pl-label text-black/70">{label}</div>
  </div>
);

const Step = ({ n, title, body, bg }) => (
  <div className={`pl-card ${bg} p-6`}>
    <div className="flex items-center gap-3 mb-3">
      <span className="w-9 h-9 rounded-full grid place-items-center bg-white border-[3px] border-black font-display">
        {n}
      </span>
      <div className="font-display text-xl uppercase">{title}</div>
    </div>
    <p className="font-mono-pl text-sm text-black/80 leading-relaxed">{body}</p>
  </div>
);

export default function HomePage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/stats")
      .then((r) => alive && setStats(r.data))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      {/* HERO */}
      <section className="grid md:grid-cols-12 gap-8 pt-10 md:pt-16 pb-16 items-center">
        <div className="md:col-span-7">
          <div className="pl-chip mb-5" data-testid="hero-chip">
            <span className="pl-dot" style={{ background: "#FF3B30" }} />
            <span>Court is now in session</span>
          </div>
          <h1
            className="font-display text-[44px] sm:text-6xl md:text-7xl leading-[0.95] tracking-tighter"
            data-testid="hero-headline"
          >
            Got a <span className="bg-[#FFE600] px-2 -mx-1 border-[3px] border-black inline-block rotate-[-2deg]">pookie</span> problem?
            <br />
            We'll <span className="text-[#FF3B30]">prosecute</span>.
          </h1>
          <p className="font-mono-pl text-base md:text-lg mt-6 max-w-xl text-black/75">
            File your case. Judge Pookie reviews the tea, weighs the receipts, and delivers a verdict so unhinged it's almost legally binding.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/file" className="pl-btn pl-btn-primary" data-testid="hero-file-cta">
              <Gavel size={18} strokeWidth={3} /> File a Lawsuit
            </Link>
            <Link to="/pricing" className="pl-btn pl-btn-secondary" data-testid="hero-pricing-cta">
              See pricing
            </Link>
          </div>

          {/* Tiny "trust" row */}
          <div className="mt-8 flex flex-wrap items-center gap-3 font-mono-pl text-xs text-black/70">
            <span className="pl-chip">🧑‍⚖️ AI Judge</span>
            <span className="pl-chip"><Lock size={11} strokeWidth={3} /> Private cases</span>
            <span className="pl-chip">🪩 Verdicts in &lt; 10s</span>
            <span className="pl-chip">⚖️ 0% Real Law</span>
          </div>
        </div>

        {/* Hero card stack */}
        <div className="md:col-span-5 relative" data-testid="hero-card-stack">
          <div className="pl-card bg-[#B4F8C8] p-5 rotate-[-3deg] absolute -left-2 top-6 w-[80%] z-10 hidden md:block">
            <div className="pl-label">Exhibit A</div>
            <p className="font-mono-pl text-sm mt-2">
              "He said 'k' after I wrote a 4 paragraph apology."
            </p>
          </div>
          <div className="pl-card bg-[#FFAEBC] p-5 rotate-[2deg] ml-auto w-[88%]">
            <div className="flex items-center justify-between">
              <div className="pl-label">Case #POO-2025-001</div>
              <Quote strokeWidth={3} size={18} />
            </div>
            <p className="font-display text-2xl leading-tight mt-3">
              "She used my skincare and didn't even compliment my glow."
            </p>
            <div className="mt-4 flex items-center justify-between">
              <span className="pl-chip">Friendship Beef</span>
              <span className="pl-stamp text-[#FF3B30] text-sm pl-wobble">Guilty</span>
            </div>
          </div>
          <div className="pl-card bg-white p-4 mt-6 rotate-[-1deg] w-[70%]">
            <div className="flex items-center gap-2">
              <Sparkles size={16} strokeWidth={3} />
              <span className="pl-label">Sentence</span>
            </div>
            <p className="font-mono-pl text-sm mt-2">
              Defendant must compliment plaintiff's outfit unprompted 5 times in 7 days.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 pb-16" data-testid="stats-row">
        <StatBlock
          label="Cases Filed"
          value={loading || !stats ? "—" : stats.total_cases}
          bg="bg-[#FFE600]"
          testid="stat-total"
        />
        <StatBlock
          label="Guilty Verdicts"
          value={loading || !stats ? "—" : stats.guilty_count}
          bg="bg-[#FFAEBC]"
          testid="stat-guilty"
        />
        <StatBlock
          label="Avg Drama Score"
          value={loading || !stats ? "—" : `${stats.avg_drama_score}/10`}
          bg="bg-[#B4F8C8]"
          testid="stat-drama"
        />
        <StatBlock
          label="Filed Today"
          value={loading || !stats ? "—" : stats.cases_today}
          bg="bg-[#A0E7E5]"
          testid="stat-today"
        />
      </section>

      {/* HOW IT WORKS */}
      <section className="pb-20" data-testid="how-it-works">
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter mb-8">
          How the Court Works
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          <Step
            n="1"
            title="File the Tea"
            body="Tell us who wronged you, what they did, and what justice would look like. Keep receipts mental."
            bg="bg-[#FFAEBC]"
          />
          <Step
            n="2"
            title="Judge Pookie Deliberates"
            body="Our extremely qualified AI judge reads your case, considers vibes, and writes a verdict like she's been doing this for years."
            bg="bg-[#FFE600]"
          />
          <Step
            n="3"
            title="Receive Your Ruling"
            body="Get a stamped verdict, dramatic reasoning, and a fully unserious sentence you can totally enforce on the group chat."
            bg="bg-[#B4F8C8]"
          />
        </div>
      </section>

      {/* PRIVACY note replacing public rulings */}
      <section className="pb-24" data-testid="privacy-note">
        <div className="pl-card bg-white p-6 md:p-10 flex flex-col md:flex-row items-start gap-6">
          <span className="w-12 h-12 rounded-full border-[3px] border-black grid place-items-center bg-[#B4F8C8]">
            <Lock size={20} strokeWidth={3} />
          </span>
          <div className="flex-1">
            <div className="pl-label">Private by default</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-1">
              Your cases stay yours, pookie.
            </h3>
            <p className="font-mono-pl text-sm md:text-base mt-2 text-black/75">
              No public feed, no featured cases, no strangers reading your drama. Sign in to see your own rulings in
              <span className="whitespace-nowrap"> "My Cases"</span> and share only the ones you want.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24" data-testid="cta-section">
        <div className="pl-card bg-[#0A0A0A] text-white p-10 md:p-14 text-center">
          <Scale className="mx-auto mb-4 text-[#FFE600]" strokeWidth={3} size={42} />
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter">
            Justice for pookies, by pookies.
          </h2>
          <p className="font-mono-pl text-sm md:text-base mt-3 text-white/80 max-w-xl mx-auto">
            Your case takes 60 seconds to file and 10 seconds to ruin someone's day. Beautifully.
          </p>
          <Link to="/file" className="pl-btn pl-btn-secondary mt-6" data-testid="cta-file">
            <Gavel strokeWidth={3} size={18} /> File Your Case
          </Link>
        </div>
      </section>
    </div>
  );
}
